import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import * as api from "../api";

const NOTIFICATIONS_PAGE_SIZE = 20;
const NOTIFICATIONS_POLL_MS = 15_000;
const CALLS_PAGE_SIZE = 200;
const CALL_ACTIVITY_REFRESH_MS = 10_000;
const CALL_ACTIVITY_PENDING_TTL_MS = 30_000;
const CALL_ACTIVITY_PRUNE_MS = 5_000;
const CALL_ACTIVITY_RECONCILE_SKEW_MS = 5_000;
const NotificationsContext = createContext(null);
const TERMINAL_CALL_STATES = new Set([
  "ended",
  "unknown",
  "rejected",
  "call_rejected",
  "ami_error",
  "ami_timeout",
  "hangup",
  "call_ended",
  "disconnect",
  "goodbye",
  "user_goodbye",
  "assistant_goodbye",
  "idle_timeout",
  "max_duration",
  "busy",
  "no_answer",
  "client_stop",
  "invalid_number",
  "network_error",
  "timeout",
  "error",
  "voicemail",
  "voicemail_detected",
  "гласова поща",
  "pipeline_start_failed",
  "audiosocket_handler_error",
]);
const DEFAULT_CLIENT_CALL_ENTRY = Object.freeze({
  status: "idle",
  active: false,
  latestCallId: "",
  resolvedAt: 0,
  callTs: 0,
  resolving: false,
  error: "",
});

function BellIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
      <path d="M10 17a2 2 0 1 0 4 0" />
    </svg>
  );
}

function formatBadgeCount(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n > 99) return "99+";
  return String(n);
}

function formatNotificationTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";

  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return "—";

  const d = new Date(parsed);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

function getNotificationTitle(item) {
  const title = String(item?.title || "").trim();
  if (title) return title;

  const eventType = String(item?.eventType || "").trim().toLowerCase();
  if (eventType === "call_reported") return "Приключил разговор";
  if (eventType === "call_started") return "Стартиран разговор";
  return "Нотификация";
}

function getNotificationMessage(item) {
  const message = String(item?.message || "").trim();
  if (message) return message;

  const clientName = String(item?.clientName || "").trim();
  const phone = String(item?.to || "").trim();
  if (clientName) return clientName;
  if (phone) return phone;
  return "Няма съобщение.";
}

function getNotificationIdentity(item, fallback = "") {
  const id = String(item?.id || "").trim();
  if (id) return `id:${id}`;

  const dedupeKey = String(item?.dedupeKey ?? item?.dedupe_key ?? "").trim();
  if (dedupeKey) return `dedupe:${dedupeKey}`;

  return fallback ? `fallback:${fallback}` : "";
}

function mergeNotificationItems(items) {
  const merged = [];
  const seen = new Set();

  items.forEach((item, index) => {
    const key = getNotificationIdentity(item, String(index));
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });

  return merged;
}

function applyOptimisticReadState(items, optimisticReadIds) {
  let forcedReadCount = 0;

  const nextItems = items.map((item) => {
    const id = String(item?.id || "").trim();
    const status = String(item?.status || "").trim().toLowerCase();

    if (id && status === "read") {
      optimisticReadIds.delete(id);
      return item;
    }
    if (!id || !optimisticReadIds.has(id)) return item;

    forcedReadCount += 1;
    return {
      ...item,
      status: "read",
      readAt: item?.readAt || item?.updatedAt || item?.createdAt || "",
    };
  });

  return { items: nextItems, forcedReadCount };
}

function toMs(value) {
  const raw = String(value || "").trim();
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) {
    if (numeric > 1e12) return numeric;
    if (numeric > 1e10) return numeric;
    return numeric * 1000;
  }
  if (!raw) return 0;

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function pickStatusSourceValue(value) {
  if (typeof value === "string") return value.trim();

  if (value && typeof value === "object") {
    const keys = ["reason", "code", "type", "value", "status"];
    for (const key of keys) {
      const raw = value?.[key];
      if (raw == null) continue;
      const str = String(raw).trim();
      if (str) return str;
    }
  }

  if (value == null) return "";
  return String(value).trim();
}

function resolveCallStatusRaw(item) {
  const endReason = pickStatusSourceValue(item?.end_reason ?? item?.endReason);
  if (endReason) return endReason;

  const state = pickStatusSourceValue(item?.state);
  if (state) return state;

  return "";
}

function readCallClientId(item) {
  const snapshot = item?.client_snapshot && typeof item.client_snapshot === "object"
    ? item.client_snapshot
    : {};

  return String(
    item?.client_id ??
    item?.clientId ??
    snapshot?._id ??
    snapshot?.id ??
    ""
  ).trim();
}

function readCallRecordId(item) {
  return String(item?.call_id ?? item?.callId ?? item?.id ?? item?._id ?? "").trim();
}

function getCallSortTs(item) {
  return Math.max(
    toMs(item?.started_at),
    toMs(item?.startedAt),
    toMs(item?.start_time),
    toMs(item?.created_at),
    toMs(item?.createdAt),
    toMs(item?.started_at_unix_secs),
    toMs(item?.start_time_unix_secs)
  );
}

function createClientCallEntry(overrides = {}) {
  return {
    ...DEFAULT_CLIENT_CALL_ENTRY,
    ...overrides,
  };
}

function buildResolvedClientCallEntry(call) {
  const resolvedAt = Date.now();
  if (!call) {
    return createClientCallEntry({
      status: "inactive",
      resolvedAt,
    });
  }

  const statusRaw = resolveCallStatusRaw(call).toLowerCase();
  const active = !TERMINAL_CALL_STATES.has(statusRaw);

  return createClientCallEntry({
    status: active ? "active" : "inactive",
    active,
    latestCallId: readCallRecordId(call),
    resolvedAt,
    callTs: getCallSortTs(call),
  });
}

function NotificationsDropdown({
  items,
  loading,
  loadingMore,
  error,
  total,
  canLoadMore,
  unreadTotal,
  pendingReadIds,
  onRetry,
  onLoadMore,
  onSelect,
}) {
  const displayedCount = items.length;
  const safeTotal = Math.max(displayedCount, Number(total) || 0);

  return (
    <div className="w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-800/60 bg-[#0f1523] shadow-2xl">
      <div className="flex items-center justify-between gap-3 border-b border-slate-800/60 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-100">Нотификации</div>
          <div className="text-xs text-slate-400">
            {unreadTotal > 0 ? `${unreadTotal} непрочетени` : "Няма непрочетени"}
          </div>
        </div>
        <button
          type="button"
          className="rounded-lg border border-[#1f2638] bg-[#121825] px-2.5 py-1 text-xs font-semibold text-slate-200 hover:bg-[#141b2d] disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onRetry}
          disabled={loading || loadingMore}
        >
          Обнови
        </button>
      </div>

      {error && (
        <div className="mx-3 mt-3 rounded-lg border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}

      <div
        className="nice-scrollbar max-h-[24rem] overflow-y-auto p-2 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {loading && items.length === 0 && (
          <div className="rounded-xl border border-[#1f2638] bg-[#0d1424] px-3 py-6 text-center text-sm text-slate-300">
            Зареждане…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-[#1f2638] bg-[#0d1424] px-3 py-6 text-center text-sm text-slate-400">
            Няма нотификации.
          </div>
        )}

        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, idx) => {
              const rowId = item?.id || item?.dedupeKey || `notification-${idx}`;
              const isRead = String(item?.status || "").trim().toLowerCase() === "read";
              const isPending = !!pendingReadIds[item?.id];
              const disabled = isRead || isPending;

              return (
                <button
                  key={rowId}
                  type="button"
                  onClick={() => onSelect(item)}
                  disabled={disabled}
                  className={[
                    "w-full rounded-xl border px-3 py-3 text-left transition",
                    isRead
                      ? "border-[#1f2638] bg-[#0d1424] text-slate-400"
                      : "border-emerald-500/20 bg-emerald-500/10 text-slate-100 hover:border-emerald-400/30 hover:bg-emerald-500/15",
                    disabled ? "cursor-default" : "",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={[
                        "mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full",
                        isRead ? "bg-slate-700" : "bg-emerald-400",
                      ].join(" ")}
                      aria-hidden="true"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-3">
                        <div className={`min-w-0 text-sm font-semibold ${isRead ? "text-slate-300" : "text-slate-100"}`}>
                          {getNotificationTitle(item)}
                        </div>
                        <div className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-slate-500">
                          {formatNotificationTime(item?.createdAt)}
                        </div>
                      </div>

                      <div className={`mt-1 text-sm leading-5 ${isRead ? "text-slate-500" : "text-slate-300"}`}>
                        {getNotificationMessage(item)}
                      </div>
                    </div>

                    {isPending && (
                      <span className="mt-1 inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80" aria-hidden="true" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(safeTotal > 0 || canLoadMore || loadingMore) && (
        <div className="border-t border-slate-800/60 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-slate-400">
                Показани {displayedCount} от {safeTotal}
              </div>
            </div>

            {canLoadMore && (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loading || loadingMore}
                className="shrink-0 rounded-lg border border-[#1f2638] bg-[#121825] px-2.5 py-1 text-xs font-semibold text-slate-200 hover:bg-[#141b2d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingMore ? "Зареждане…" : "Зареди още"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function NotificationsProvider({ enabled = false, children }) {
  const [items, setItems] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [pendingReadIds, setPendingReadIds] = useState({});
  const [pendingCallClients, setPendingCallClients] = useState({});
  const [clientCallState, setClientCallState] = useState({});

  const itemsRef = useRef([]);
  const clientCallStateRef = useRef({});
  const pendingReadIdsRef = useRef({});
  const pendingCallClientsRef = useRef({});
  const pageRef = useRef(1);
  const totalPagesRef = useRef(1);
  const loadNotificationsRef = useRef(async () => {});
  const ensureClientCallStateRef = useRef(async () => null);
  const generationRef = useRef(0);
  const fetchInFlightRef = useRef(false);
  const optimisticReadIdsRef = useRef(new Set());
  const clientCallLookupPromisesRef = useRef({});
  const lastLoadedAtRef = useRef(0);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  useEffect(() => {
    clientCallStateRef.current = clientCallState;
  }, [clientCallState]);

  useEffect(() => {
    pendingReadIdsRef.current = pendingReadIds;
  }, [pendingReadIds]);

  useEffect(() => {
    pendingCallClientsRef.current = pendingCallClients;
  }, [pendingCallClients]);

  useEffect(() => {
    if (enabled) return;

    generationRef.current += 1;
    fetchInFlightRef.current = false;
    optimisticReadIdsRef.current = new Set();
    clientCallLookupPromisesRef.current = {};
    lastLoadedAtRef.current = 0;
    setItems([]);
    setUnreadTotal(0);
    setPage(1);
    setTotal(0);
    setTotalPages(1);
    setLoading(false);
    setLoadingMore(false);
    setError("");
    setPendingReadIds({});
    setPendingCallClients({});
    setClientCallState({});
  }, [enabled]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setPendingCallClients((prev) => {
        const entries = Object.entries(prev).filter(([, entry]) => Number(entry?.expiresAt || 0) > now);
        if (entries.length === Object.keys(prev).length) return prev;
        return Object.fromEntries(entries);
      });
    }, CALL_ACTIVITY_PRUNE_MS);

    return () => window.clearInterval(timer);
  }, []);

  loadNotificationsRef.current = async ({ silent = false, mode = "refresh" } = {}) => {
    if (!enabled || fetchInFlightRef.current) return;

    const normalizedMode = mode === "loadMore" ? "loadMore" : "refresh";
    const loadedPages = Math.max(1, Number(pageRef.current || 1));
    const knownTotalPages = Math.max(1, Number(totalPagesRef.current || 1));
    const requestedPages = normalizedMode === "loadMore"
      ? Math.min(loadedPages + 1, knownTotalPages)
      : loadedPages;

    if (normalizedMode === "loadMore" && requestedPages <= loadedPages) return;

    const generation = generationRef.current;
    fetchInFlightRef.current = true;

    if (normalizedMode === "loadMore") {
      setLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }
    setError("");

    try {
      let pagesToLoad = requestedPages;
      let nextRawItems = [];
      let latestResponse = null;

      for (let currentPage = 1; currentPage <= pagesToLoad; currentPage += 1) {
        const data = await api.listNotifications({ page: currentPage, per_page: NOTIFICATIONS_PAGE_SIZE });
        if (generation !== generationRef.current) return;

        if (currentPage === 1) {
          const apiTotalPages = Math.max(1, Number(data?.total_pages || 1));
          pagesToLoad = Math.min(pagesToLoad, apiTotalPages);
        }

        const chunk = Array.isArray(data?.items) ? data.items : [];
        nextRawItems = nextRawItems.concat(chunk);
        latestResponse = data;
      }

      const optimisticReadIds = optimisticReadIdsRef.current;
      const mergedItems = mergeNotificationItems(nextRawItems);
      const { items: nextItems, forcedReadCount } = applyOptimisticReadState(mergedItems, optimisticReadIds);
      const rawTotal = Number(latestResponse?.total);
      const nextTotal = Number.isFinite(rawTotal) && rawTotal >= 0
        ? rawTotal
        : nextItems.length;
      const rawTotalPages = Number(latestResponse?.total_pages);
      const nextTotalPages = Number.isFinite(rawTotalPages) && rawTotalPages > 0
        ? rawTotalPages
        : (NOTIFICATIONS_PAGE_SIZE > 0 ? Math.max(1, Math.ceil(Math.max(nextTotal, nextItems.length) / NOTIFICATIONS_PAGE_SIZE)) : 1);
      const nextPage = Math.min(pagesToLoad, nextTotalPages);
      const unreadTotalRaw = Number(latestResponse?.unread_total);
      const nextUnreadTotal = Number.isFinite(unreadTotalRaw) && unreadTotalRaw >= 0
        ? unreadTotalRaw
        : nextItems.filter((item) => String(item?.status || "").trim().toLowerCase() !== "read").length;

      setItems(nextItems);
      setPage(Math.max(1, nextPage));
      setTotal(Math.max(nextItems.length, nextTotal));
      setTotalPages(nextTotalPages);
      setUnreadTotal(Math.max(0, nextUnreadTotal - forcedReadCount));
      lastLoadedAtRef.current = Date.now();
    } catch (e) {
      if (generation !== generationRef.current) return;
      setError(e?.message || "Грешка при зареждане на нотификациите.");
    } finally {
      fetchInFlightRef.current = false;
      if (generation === generationRef.current && normalizedMode === "loadMore") {
        setLoadingMore(false);
      }
      if (generation === generationRef.current && normalizedMode !== "loadMore" && !silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!enabled) return;

    void loadNotificationsRef.current({ silent: false, mode: "refresh" });

    const intervalId = setInterval(() => {
      if (document.hidden) return;
      void loadNotificationsRef.current({ silent: true, mode: "refresh" });
    }, NOTIFICATIONS_POLL_MS);

    const onVisible = () => {
      if (!document.hidden) {
        void loadNotificationsRef.current({ silent: true, mode: "refresh" });
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      generationRef.current += 1;
      fetchInFlightRef.current = false;
      clientCallLookupPromisesRef.current = {};
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(intervalId);
    };
  }, [enabled]);

  ensureClientCallStateRef.current = async (clientId, { force = false } = {}) => {
    const id = String(clientId || "").trim();
    if (!enabled || !id) return null;

    const pendingPromise = clientCallLookupPromisesRef.current[id];
    if (pendingPromise) return pendingPromise;

    const currentEntry = clientCallStateRef.current[id];
    if (!force && currentEntry?.resolvedAt && (Date.now() - currentEntry.resolvedAt) < CALL_ACTIVITY_REFRESH_MS) {
      return currentEntry;
    }

    const generation = generationRef.current;
    setClientCallState((prev) => ({
      ...prev,
      [id]: createClientCallEntry({
        ...(prev[id] || {}),
        resolving: true,
        error: "",
      }),
    }));

    let lookupPromise;
    lookupPromise = (async () => {
      try {
        let page = 1;
        let totalPages = 1;
        let latestCall = null;
        let latestCallTs = -1;

        while (page <= totalPages) {
          const res = await api.listCalls({
            page,
            per_page: CALLS_PAGE_SIZE,
            q: null,
            type: "phone_call",
          });

          if (generation !== generationRef.current) {
            return clientCallStateRef.current[id] || null;
          }

          const chunk = Array.isArray(res?.items) ? res.items : [];
          for (const call of chunk) {
            if (readCallClientId(call) !== id) continue;

            const callTs = getCallSortTs(call);
            if (!latestCall || callTs > latestCallTs) {
              latestCall = call;
              latestCallTs = callTs;
            }
          }

          totalPages = Math.max(1, Number(res?.total_pages || 1));
          page += 1;
        }

        const nextEntry = buildResolvedClientCallEntry(latestCall);
        if (generation === generationRef.current) {
          setClientCallState((prev) => ({
            ...prev,
            [id]: nextEntry,
          }));
        }
        return nextEntry;
      } catch (e) {
        const nextEntry = createClientCallEntry({
          ...(clientCallStateRef.current[id] || {}),
          status: "error",
          active: false,
          resolving: false,
          resolvedAt: Date.now(),
          error: e?.message || "Грешка при проверка на разговора.",
        });
        if (generation === generationRef.current) {
          setClientCallState((prev) => ({
            ...prev,
            [id]: nextEntry,
          }));
        }
        return nextEntry;
      } finally {
        if (clientCallLookupPromisesRef.current[id] === lookupPromise) {
          delete clientCallLookupPromisesRef.current[id];
        }
      }
    })();

    clientCallLookupPromisesRef.current[id] = lookupPromise;
    return lookupPromise;
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      const trackedClientIds = new Set();

      Object.entries(clientCallStateRef.current).forEach(([clientId, entry]) => {
        if (String(entry?.status || "").trim().toLowerCase() === "active") {
          trackedClientIds.add(clientId);
        }
      });

      Object.entries(pendingCallClientsRef.current).forEach(([clientId, entry]) => {
        if (Number(entry?.expiresAt || 0) > now) {
          trackedClientIds.add(clientId);
        }
      });

      trackedClientIds.forEach((clientId) => {
        void ensureClientCallStateRef.current(clientId, { force: true });
      });
    }, CALL_ACTIVITY_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled]);

  const refresh = useCallback(
    ({ silent = false } = {}) => loadNotificationsRef.current({ silent, mode: "refresh" }),
    []
  );
  const loadMore = useCallback(
    () => loadNotificationsRef.current({ silent: true, mode: "loadMore" }),
    []
  );
  const markClientCallPending = useCallback((clientId, ttlMs = CALL_ACTIVITY_PENDING_TTL_MS) => {
    const id = String(clientId || "").trim();
    if (!id) return;

    const ttl = Math.max(1_000, Number(ttlMs) || CALL_ACTIVITY_PENDING_TTL_MS);
    const now = Date.now();
    setPendingCallClients((prev) => ({
      ...prev,
      [id]: {
        markedAt: now,
        expiresAt: now + ttl,
      },
    }));
  }, []);
  const isClientCallActive = useCallback((clientId) => {
    const id = String(clientId || "").trim();
    if (!id) return false;

    const pendingEntry = pendingCallClientsRef.current[id];
    const pendingActive = Number(pendingEntry?.expiresAt || 0) > Date.now();
    const entry = clientCallStateRef.current[id];

    if (entry?.status === "active") return true;
    if (entry?.status === "inactive") {
      if (pendingActive && Number(entry?.callTs || 0) < Number(pendingEntry?.markedAt || 0) - CALL_ACTIVITY_RECONCILE_SKEW_MS) {
        return true;
      }
      return false;
    }

    return pendingActive;
  }, []);
  const ensureClientCallState = useCallback((clientId, options = {}) => (
    ensureClientCallStateRef.current(clientId, options)
  ), []);

  const markAsRead = async (item) => {
    const id = String(item?.id || "").trim();
    if (!id || pendingReadIdsRef.current[id]) return;

    const currentItem = itemsRef.current.find((entry) => String(entry?.id || "").trim() === id);
    const wasUnread = String(currentItem?.status || "").trim().toLowerCase() !== "read";
    if (!currentItem || !wasUnread) return;

    const nowIso = new Date().toISOString();

    optimisticReadIdsRef.current.add(id);
    setPendingReadIds((prev) => ({ ...prev, [id]: true }));
    setError("");
    setItems((prev) => prev.map((entry) => (
      String(entry?.id || "").trim() === id
        ? { ...entry, status: "read", readAt: entry?.readAt || nowIso, updatedAt: nowIso }
        : entry
    )));
    setUnreadTotal((prev) => Math.max(0, prev - 1));

    try {
      await api.updateNotificationStatus(id, "read");
      await loadNotificationsRef.current({ silent: true, mode: "refresh" });
    } catch (e) {
      optimisticReadIdsRef.current.delete(id);
      setError(e?.message || "Грешка при маркиране на нотификацията.");
      setItems((prev) => prev.map((entry) => (
        String(entry?.id || "").trim() === id
          ? {
            ...entry,
            status: currentItem?.status || "unread",
            readAt: currentItem?.readAt || "",
            updatedAt: currentItem?.updatedAt || entry?.updatedAt || "",
          }
          : entry
      )));
      setUnreadTotal((prev) => prev + 1);
    } finally {
      setPendingReadIds((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        items,
        unreadTotal,
        page,
        total,
        totalPages,
        canLoadMore: page < totalPages,
        loading,
        loadingMore,
        error,
        pendingReadIds,
        hasLoaded: lastLoadedAtRef.current > 0,
        refresh,
        loadMore,
        markAsRead,
        clientCallState,
        isClientCallActive,
        markClientCallPending,
        ensureClientCallState,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationsProvider");
  }
  return ctx;
}

export default function NotificationsBell({ enabled = false, forceClosed = false }) {
  const {
    items,
    unreadTotal,
    total,
    canLoadMore,
    loading,
    loadingMore,
    error,
    pendingReadIds,
    hasLoaded,
    refresh,
    loadMore,
    markAsRead,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!enabled || forceClosed) {
      setOpen(false);
    }
  }, [enabled, forceClosed]);

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("touchstart", onDoc, true);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("touchstart", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!enabled) return null;

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        void refresh({ silent: hasLoaded || items.length > 0 || loading });
      }
      return next;
    });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Нотификации"
        aria-expanded={open}
        className={[
          "relative flex h-11 w-11 items-center justify-center rounded-xl border transition focus:outline-none focus:ring-2 focus:ring-violet-600/40",
          open
            ? "border-violet-500/40 bg-violet-600/15 text-violet-100"
            : "border-[#1f2638] bg-[#121825] text-slate-200 hover:bg-[#141b2d]",
        ].join(" ")}
        title="Нотификации"
      >
        <BellIcon className="shrink-0" />
        {formatBadgeCount(unreadTotal) && (
          <span className="absolute right-0.5 top-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold text-[#08110f]">
            {formatBadgeCount(unreadTotal)}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-3">
          <NotificationsDropdown
            items={items}
            loading={loading}
            loadingMore={loadingMore}
            error={error}
            total={total}
            canLoadMore={canLoadMore}
            unreadTotal={unreadTotal}
            pendingReadIds={pendingReadIds}
            onRetry={() => void refresh({ silent: false })}
            onLoadMore={() => void loadMore()}
            onSelect={(item) => void markAsRead(item)}
          />
        </div>
      )}
    </div>
  );
}
